import { useState } from "react";
import { Mail, Linkedin, MapPin, Quote, Play } from "lucide-react";
import { founder } from "../../data/founder";
import FounderVideoModal, { toYouTubeEmbed } from "./FounderVideoModal";

const LOGO = "/logo.png";

export default function FounderStory() {
  const [videoOpen, setVideoOpen] = useState(false);
  const hasVideo = !!toYouTubeEmbed(founder.videoUrl);
  return (
    <section data-testid="founder-story" className="border-t-2 border-[var(--c-border)] px-4 py-16 sm:py-20">
      {videoOpen && hasVideo && <FounderVideoModal url={founder.videoUrl} title={founder.videoLabel} onClose={() => setVideoOpen(false)} />}
      <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        {/* Photo card */}
        <div className="relative mx-auto w-full max-w-sm lg:mx-0">
          <div className="absolute -left-3 -top-3 h-full w-full rounded-3xl border-2 border-[var(--c-border)] bg-[#A3E6D5]/60" aria-hidden="true" />
          <div className="absolute -bottom-3 -right-3 h-full w-full rounded-3xl bg-[#1E3A8A]" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-3xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[6px_6px_0px_var(--c-shadow)]">
            <div className="relative">
            {founder.photo ? (
              <img
                data-testid="founder-photo"
                src={founder.photo}
                alt={founder.name}
                className="aspect-[4/5] w-full object-cover"
              />
            ) : (
              <div data-testid="founder-photo-placeholder" className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-4 bg-white p-8">
                <img src={LOGO} alt="Lewis Expert Enterprise" className="w-3/4 object-contain" />
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1E3A8A]">Founder &amp; Author</p>
              </div>
            )}
            {hasVideo && (
              <button
                data-testid="founder-video-play"
                onClick={() => setVideoOpen(true)}
                aria-label={founder.videoLabel}
                className="group absolute inset-0 flex items-end justify-start p-4"
              >
                <span className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-white px-3 py-2 text-xs font-bold text-[#1E3A8A] shadow-[3px_3px_0px_var(--c-shadow)] transition-transform group-hover:-translate-y-0.5">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1E3A8A] text-white"><Play className="ml-0.5 h-3 w-3 fill-current" /></span>
                  {founder.videoLabel}
                </span>
              </button>
            )}
            </div>
            <div className="border-t-2 border-[var(--c-border)] bg-[var(--c-bg)] px-5 py-4">
              <p className="font-heading text-lg font-black text-[var(--c-text)]">{founder.name}</p>
              <p className="text-sm text-[var(--c-muted)]">{founder.title}</p>
              <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-[var(--c-muted)]">
                <MapPin className="h-3 w-3" /> {founder.location}
              </p>
            </div>
          </div>
        </div>

        {/* Story */}
        <div>
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#1E3A8A] shadow-[2px_2px_0px_var(--c-shadow)]">
            Meet the Founder
          </p>
          <h2 className="font-heading text-3xl font-black leading-tight text-[var(--c-text)] sm:text-4xl">
            {founder.headline}
          </h2>
          <div className="mt-6 space-y-4">
            {founder.paragraphs.map((p, i) => (
              <p key={p.slice(0, 40)} className={`text-base leading-relaxed ${i === 0 ? "text-[var(--c-text)]" : "text-[var(--c-muted)]"}`}>
                {p}
              </p>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3">
            {founder.stats.map((s) => (
              <div key={s.label} className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-3 shadow-[3px_3px_0px_var(--c-shadow)]">
                <p className="font-heading text-2xl font-black text-[#1E3A8A]">{s.value}</p>
                <p className="mt-0.5 text-[11px] font-semibold leading-snug text-[var(--c-muted)]">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {hasVideo && (
              <button
                data-testid="founder-video-button"
                onClick={() => setVideoOpen(true)}
                className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-5 py-2.5 text-sm font-bold text-white shadow-[3px_3px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5"
              >
                <Play className="h-4 w-4 fill-current" /> {founder.videoLabel}
              </button>
            )}
            <a
              href={`mailto:${founder.email}`}
              data-testid="founder-email-link"
              className={`inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] px-5 py-2.5 text-sm font-bold shadow-[3px_3px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5 ${
                hasVideo ? "bg-[var(--c-surface)] text-[var(--c-text)]" : "bg-[#1E3A8A] text-white"
              }`}
            >
              <Mail className="h-4 w-4" /> Say hello
            </a>
            {founder.linkedin && (
              <a
                href={founder.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-linkedin-link"
                className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-5 py-2.5 text-sm font-bold text-[var(--c-text)] shadow-[3px_3px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5"
              >
                <Linkedin className="h-4 w-4" /> LinkedIn
              </a>
            )}
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--c-muted)]">
              <Quote className="h-3.5 w-3.5" /> Founder-led &middot; Independent &middot; Texas-only
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
